import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useClientAccess } from '../hooks/useClientAccess';
import { useI18n } from '../contexts/I18nContext';
import { uiSettingsService, invalidateEffective } from '../services/uiSettingsService';
import {
  UILanguage,
  UILayoutVariant,
  UIThemeMode,
  UISettingsPayload,
  UISettingsScope,
} from '../types/uiSettings';
import '../styles/settings.css';

type TabType = 'global' | 'role';

const THEME_MODES: UIThemeMode[] = ['light', 'dark', 'auto'];
const LAYOUT_VARIANTS: UILayoutVariant[] = ['default', 'compact', 'comfortable'];
const LANGUAGES: UILanguage[] = ['en-GB', 'fr'];

const emptyPayload: UISettingsPayload = {
  theme_mode: null,
  variant: null,
  animations: null,
  language: null,
  show_api_status: null,
  show_debug_menu: null,
  show_language_selector: null,
  debug_menu_usernames: null,
  show_raw_tab: null,
  raw_tab_usernames: null,
  show_json_config: null,
  json_config_usernames: null,
};

// Parse a comma- or newline-separated string of usernames into a clean array.
// Empty string → null so the backend treats it as "unset" (fall back to the
// show_debug_menu boolean for everyone).
const parseUsernameList = (raw: string): string[] | null => {
  const list = raw
    .split(/[\n,]/)
    .map(s => s.trim())
    .filter(s => s.length > 0);
  return list.length > 0 ? list : null;
};

const formatUsernameList = (v: string[] | null | undefined): string =>
  Array.isArray(v) ? v.join(', ') : '';

// Helper: convert a ``'' | 'on' | 'off'`` select value into ``null | true | false``.
const parseTriState = (v: string): boolean | null => (v === '' ? null : v === 'on');
// Helper: render a nullable boolean back into the select's sentinel value.
const triStateValue = (v: boolean | null | undefined): string =>
  v === null || v === undefined ? '' : v ? 'on' : 'off';

export const SettingsPage: React.FC = () => {
  const { t } = useI18n();
  const { isSuperAdmin } = useClientAccess();

  const [activeTab, setActiveTab] = useState<TabType>('global');
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<string>('Admin');
  const [scopes, setScopes] = useState<UISettingsScope[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const [form, setForm] = useState<UISettingsPayload>(emptyPayload);

  // Load all scope rows on mount / after saves.
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await uiSettingsService.listAll();
      setScopes(data.scopes);
      setAvailableRoles(data.available_roles);
      if (!data.available_roles.includes(selectedRole) && data.available_roles.length > 0) {
        setSelectedRole(data.available_roles[0]);
      }
    } catch (err) {
      setBanner({ kind: 'error', text: t('settings.errorLoading') });
    } finally {
      setLoading(false);
    }
  }, [selectedRole, t]);

  useEffect(() => {
    if (isSuperAdmin) {
      loadAll();
    }
  }, [isSuperAdmin, loadAll]);

  // Sync the form to the currently selected scope whenever the tab / role
  // changes or new scopes are loaded.
  useEffect(() => {
    const scopeValue = activeTab === 'global' ? 'global' : selectedRole;
    const row = scopes.find(s => s.scope_type === activeTab && s.scope_value === scopeValue);
    setForm({ ...emptyPayload, ...(row?.settings ?? {}) });
  }, [activeTab, selectedRole, scopes]);

  const currentScopeValue = activeTab === 'global' ? 'global' : selectedRole;
  const currentRow = useMemo(
    () => scopes.find(s => s.scope_type === activeTab && s.scope_value === currentScopeValue),
    [scopes, activeTab, currentScopeValue]
  );

  const handleFieldChange = <K extends keyof UISettingsPayload>(
    field: K,
    value: UISettingsPayload[K]
  ) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setBanner(null);
    try {
      // Convert empty-string sentinel values back to null so the backend
      // stores "unset" (allowing the next layer to supply it).
      const payload: UISettingsPayload = {
        theme_mode: form.theme_mode || null,
        variant: form.variant || null,
        animations: typeof form.animations === 'boolean' ? form.animations : null,
        language: form.language || null,
        show_api_status: typeof form.show_api_status === 'boolean' ? form.show_api_status : null,
        show_debug_menu: typeof form.show_debug_menu === 'boolean' ? form.show_debug_menu : null,
        show_language_selector:
          typeof form.show_language_selector === 'boolean' ? form.show_language_selector : null,
        debug_menu_usernames: Array.isArray(form.debug_menu_usernames) && form.debug_menu_usernames.length > 0
          ? form.debug_menu_usernames
          : null,
        show_raw_tab: typeof form.show_raw_tab === 'boolean' ? form.show_raw_tab : null,
        raw_tab_usernames: Array.isArray(form.raw_tab_usernames) && form.raw_tab_usernames.length > 0
          ? form.raw_tab_usernames
          : null,
        show_json_config: typeof form.show_json_config === 'boolean' ? form.show_json_config : null,
        json_config_usernames: Array.isArray(form.json_config_usernames) && form.json_config_usernames.length > 0
          ? form.json_config_usernames
          : null,
      };
      await uiSettingsService.upsert(activeTab, currentScopeValue, payload);
      // Drop the cached /v2/ui-settings/effective response and broadcast
      // app:auth-ready so the UiVisibility/Theme/I18n contexts re-fetch their
      // effective settings immediately. Without this, the new allowlist /
      // visibility toggle only takes effect on the next page reload.
      invalidateEffective();
      window.dispatchEvent(new Event('app:auth-ready'));
      setBanner({ kind: 'success', text: t('settings.saved') });
      await loadAll();
    } catch (err) {
      setBanner({ kind: 'error', text: t('settings.errorSaving') });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!currentRow) return;
    if (!window.confirm(t('settings.clearConfirm'))) return;
    setSaving(true);
    setBanner(null);
    try {
      await uiSettingsService.remove(activeTab, currentScopeValue);
      invalidateEffective();
      window.dispatchEvent(new Event('app:auth-ready'));
      setBanner({ kind: 'success', text: t('settings.cleared') });
      await loadAll();
    } catch (err) {
      setBanner({ kind: 'error', text: t('settings.errorSaving') });
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="page settings-page">
        <div className="settings-denied">
          <div className="denied-icon">🔒</div>
          <h2>{t('settings.denied')}</h2>
          <p>{t('settings.deniedDetail')}</p>
        </div>
      </div>
    );
  }

  const renderPreviewRow = (label: string, value: string | null | undefined) => (
    <div className="preview-row">
      <span className="preview-key">{label}</span>
      <span className="preview-value">{value ?? t('settings.preview.unset')}</span>
    </div>
  );

  const animationsLabel =
    form.animations === true ? t('settings.on')
    : form.animations === false ? t('settings.off')
    : null;

  return (
    <div className="page settings-page">
      <div className="page-header">
        <div className="page-title">
          <h1>{t('settings.title')}</h1>
          <p>{t('settings.subtitle')}</p>
        </div>
      </div>

      <div className="device-types-page-header">
        <div className="page-tabs">
          <button
            className={`page-tab ${activeTab === 'global' ? 'active' : ''}`}
            onClick={() => setActiveTab('global')}
          >
            {t('settings.tabGlobal')}
          </button>
          <button
            className={`page-tab ${activeTab === 'role' ? 'active' : ''}`}
            onClick={() => setActiveTab('role')}
          >
            {t('settings.tabByRole')}
          </button>
        </div>
      </div>

      {activeTab === 'role' && (
        <div className="settings-subheader">
          <label htmlFor="role-picker">{t('settings.rolePicker')}:</label>
          <select
            id="role-picker"
            value={selectedRole}
            onChange={e => setSelectedRole(e.target.value)}
          >
            {availableRoles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        </div>
      )}

      <div className="page-content">
        {banner && (
          <div className={`settings-banner ${banner.kind}`}>{banner.text}</div>
        )}

        <div className="settings-grid">
          <div className="settings-card">
            <h2>
              {activeTab === 'global'
                ? t('settings.scope.global')
                : t('settings.scope.role', { role: selectedRole })}
            </h2>
            <p className="settings-card-subtitle">{t('settings.formHint')}</p>

            <div className="settings-field">
              <label htmlFor="theme-mode">{t('settings.themeMode')}</label>
              <select
                id="theme-mode"
                value={form.theme_mode ?? ''}
                onChange={e => handleFieldChange('theme_mode', (e.target.value || null) as UIThemeMode | null)}
              >
                <option value="">{t('settings.unset')}</option>
                {THEME_MODES.map(m => (
                  <option key={m} value={m}>{t(`theme.${m}Mode`)}</option>
                ))}
              </select>
            </div>

            <div className="settings-field">
              <label htmlFor="variant">{t('settings.layoutDensity')}</label>
              <select
                id="variant"
                value={form.variant ?? ''}
                onChange={e => handleFieldChange('variant', (e.target.value || null) as UILayoutVariant | null)}
              >
                <option value="">{t('settings.unset')}</option>
                {LAYOUT_VARIANTS.map(v => (
                  <option key={v} value={v}>{t(`theme.${v}`)}</option>
                ))}
              </select>
            </div>

            <div className="settings-field">
              <label htmlFor="animations">{t('settings.animations')}</label>
              <select
                id="animations"
                value={form.animations === null || form.animations === undefined ? '' : form.animations ? 'on' : 'off'}
                onChange={e => {
                  const v = e.target.value;
                  handleFieldChange('animations', v === '' ? null : v === 'on');
                }}
              >
                <option value="">{t('settings.unset')}</option>
                <option value="on">{t('settings.on')}</option>
                <option value="off">{t('settings.off')}</option>
              </select>
            </div>

            <div className="settings-field">
              <label htmlFor="language">{t('settings.language')}</label>
              <select
                id="language"
                value={form.language ?? ''}
                onChange={e => handleFieldChange('language', (e.target.value || null) as UILanguage | null)}
              >
                <option value="">{t('settings.unset')}</option>
                {LANGUAGES.map(l => (
                  <option key={l} value={l}>{l === 'en-GB' ? 'English (UK)' : 'Français'}</option>
                ))}
              </select>
            </div>

            <h3 className="settings-section-title">{t('settings.topBar')}</h3>

            <div className="settings-field">
              <label htmlFor="show-api-status">{t('settings.showApiStatus')}</label>
              <select
                id="show-api-status"
                value={triStateValue(form.show_api_status)}
                onChange={e => handleFieldChange('show_api_status', parseTriState(e.target.value))}
              >
                <option value="">{t('settings.unset')}</option>
                <option value="on">{t('settings.shown')}</option>
                <option value="off">{t('settings.hidden')}</option>
              </select>
            </div>

            <div className="settings-field">
              <label htmlFor="show-debug-menu">{t('settings.showDebugMenu')}</label>
              <select
                id="show-debug-menu"
                value={triStateValue(form.show_debug_menu)}
                onChange={e => handleFieldChange('show_debug_menu', parseTriState(e.target.value))}
              >
                <option value="">{t('settings.unset')}</option>
                <option value="on">{t('settings.shown')}</option>
                <option value="off">{t('settings.hidden')}</option>
              </select>
            </div>

            <div className="settings-field">
              <label htmlFor="debug-menu-usernames">{t('settings.debugMenuUsernames')}</label>
              <input
                id="debug-menu-usernames"
                type="text"
                placeholder={t('settings.debugMenuUsernamesPlaceholder')}
                value={formatUsernameList(form.debug_menu_usernames)}
                onChange={e => handleFieldChange('debug_menu_usernames', parseUsernameList(e.target.value))}
              />
              <small className="settings-hint">{t('settings.debugMenuUsernamesHint')}</small>
            </div>

            <div className="settings-field">
              <label htmlFor="show-language-selector">{t('settings.showLanguageSelector')}</label>
              <select
                id="show-language-selector"
                value={triStateValue(form.show_language_selector)}
                onChange={e => handleFieldChange('show_language_selector', parseTriState(e.target.value))}
              >
                <option value="">{t('settings.unset')}</option>
                <option value="on">{t('settings.shown')}</option>
                <option value="off">{t('settings.hidden')}</option>
              </select>
            </div>

            <div className="settings-field">
              <label htmlFor="show-raw-tab">{t('settings.showRawTab')}</label>
              <select
                id="show-raw-tab"
                value={triStateValue(form.show_raw_tab)}
                onChange={e => handleFieldChange('show_raw_tab', parseTriState(e.target.value))}
              >
                <option value="">{t('settings.unset')}</option>
                <option value="on">{t('settings.shown')}</option>
                <option value="off">{t('settings.hidden')}</option>
              </select>
              <small className="settings-hint">{t('settings.showRawTabHint')}</small>
            </div>

            <div className="settings-field">
              <label htmlFor="raw-tab-usernames">{t('settings.rawTabUsernames')}</label>
              <input
                id="raw-tab-usernames"
                type="text"
                placeholder={t('settings.rawTabUsernamesPlaceholder')}
                value={formatUsernameList(form.raw_tab_usernames)}
                onChange={e => handleFieldChange('raw_tab_usernames', parseUsernameList(e.target.value))}
              />
              <small className="settings-hint">{t('settings.rawTabUsernamesHint')}</small>
            </div>

            <div className="settings-field">
              <label htmlFor="show-json-config">{t('settings.showJsonConfig')}</label>
              <select
                id="show-json-config"
                value={triStateValue(form.show_json_config)}
                onChange={e => handleFieldChange('show_json_config', parseTriState(e.target.value))}
              >
                <option value="">{t('settings.unset')}</option>
                <option value="on">{t('settings.shown')}</option>
                <option value="off">{t('settings.hidden')}</option>
              </select>
              <small className="settings-hint">{t('settings.showJsonConfigHint')}</small>
            </div>

            <div className="settings-field">
              <label htmlFor="json-config-usernames">{t('settings.jsonConfigUsernames')}</label>
              <input
                id="json-config-usernames"
                type="text"
                placeholder={t('settings.jsonConfigUsernamesPlaceholder')}
                value={formatUsernameList(form.json_config_usernames)}
                onChange={e => handleFieldChange('json_config_usernames', parseUsernameList(e.target.value))}
              />
              <small className="settings-hint">{t('settings.jsonConfigUsernamesHint')}</small>
            </div>

            <div className="settings-resolution">{t('settings.resolution')}</div>

            <div className="settings-footer">
              {currentRow && (
                <button
                  type="button"
                  className="settings-button danger"
                  disabled={saving || loading}
                  onClick={handleClear}
                >
                  {t('settings.clearOverride')}
                </button>
              )}
              <button
                type="button"
                className="settings-button primary"
                disabled={saving || loading}
                onClick={handleSave}
              >
                {saving ? t('settings.saving') : t('settings.save')}
              </button>
            </div>
          </div>

          <aside className="settings-preview">
            <h3>{t('settings.preview.title')}</h3>
            {renderPreviewRow(t('settings.themeMode'), form.theme_mode ? t(`theme.${form.theme_mode}Mode`) : null)}
            {renderPreviewRow(t('settings.layoutDensity'), form.variant ? t(`theme.${form.variant}`) : null)}
            {renderPreviewRow(t('settings.animations'), animationsLabel)}
            {renderPreviewRow(t('settings.language'),
              form.language === 'fr' ? 'Français' : form.language === 'en-GB' ? 'English (UK)' : null)}
            {renderPreviewRow(
              t('settings.showApiStatus'),
              form.show_api_status === true ? t('settings.shown')
                : form.show_api_status === false ? t('settings.hidden') : null
            )}
            {renderPreviewRow(
              t('settings.showDebugMenu'),
              form.show_debug_menu === true ? t('settings.shown')
                : form.show_debug_menu === false ? t('settings.hidden') : null
            )}
            {renderPreviewRow(
              t('settings.showLanguageSelector'),
              form.show_language_selector === true ? t('settings.shown')
                : form.show_language_selector === false ? t('settings.hidden') : null
            )}
            {currentRow?.updated_at && (
              <div className="settings-resolution" style={{ marginTop: '1rem' }}>
                {t('settings.preview.lastUpdated', { when: new Date(currentRow.updated_at).toLocaleString() })}
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};
